/**
 * Market conditions notes for every user: the header pill, its popover, and the
 * toast raised by useMarketConditionsPush all read from here.
 *
 * Deliberately separate from pages/scheduling/capacity/model.ts, which carries
 * the full capacity queue and is only granted to Scheduling users. This store
 * hits the notes-only GET that migration 565 granted to all enabled groups, so
 * it is safe to mount in the dashboard shell on every page.
 *
 * The 60s poll is the fallback when the socket push is unavailable; a push
 * calls refreshMarketConditions() directly.
 */
import { useEffect, useSyncExternalStore } from 'react';
import api from './api';
import { markets } from '../pages/scheduling/capacity/model';

export interface MarketNoteImage { id: number; name: string }
export interface MarketConditionsNote {
  text: string;
  author: string;
  updatedAt: number;
  version: number;
  images: MarketNoteImage[];
  conditionStatus?: 'green'|'yellow'|'red';
  conditionDay?: string | null;
  daysOut?: number|null;
}
export type MarketConditionsNotes = Record<string, MarketConditionsNote>;

interface State { notes: MarketConditionsNotes; loaded: boolean; failed: boolean }

const POLL_MS = 60000;
let state: State = { notes: {}, loaded: false, failed: false };
const listeners = new Set<() => void>();
let poll: ReturnType<typeof setInterval> | undefined;
let pending: Promise<void> | undefined;

const emit = () => listeners.forEach(fn => fn());
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  if (!poll) poll = setInterval(() => void refreshMarketConditions(), POLL_MS);
  return () => {
    listeners.delete(fn);
    if (!listeners.size) { clearInterval(poll); poll = undefined; }
  };
};

let loadedAt = 0;
/** A mount-time read is skipped while the snapshot is younger than this. */
const FRESH_MS = 30000;

export async function refreshMarketConditions(): Promise<void> {
  if (pending) return pending;
  pending = (async () => {
    try {
      const { data } = await api.get('/market-conditions');
      loadedAt = Date.now();
      // Skip the swap when a poll found nothing new, so the pill, the board's
      // tabs and every conditions panel stop re-rendering on an unchanged read.
      const notes = data?.notes ?? {};
      if (state.loaded && !state.failed && JSON.stringify(notes) === JSON.stringify(state.notes)) return;
      state = { notes, loaded: true, failed: false };
    } catch {
      // Keep the last good snapshot; the next poll or push retries. `failed`
      // lets the pill tell "every market is green" apart from "we could not
      // read them" -- without it, a 403 renders as eight reassuring green
      // cards, which is the one answer this feature must never give.
      state = { ...state, loaded: true, failed: true };
    }
    emit();
  })();
  try { await pending; } finally { pending = undefined; }
}

/**
 * Mount-time load. The pill, the capacity board's tabs and one panel per
 * market all read this, and the panels remount on every tab change, so an
 * unconditional fetch per mount far outran the 60s poll. A save or a socket
 * push still calls refreshMarketConditions() directly and always fetches.
 */
export async function ensureMarketConditions(): Promise<void> {
  if (state.loaded && !state.failed && Date.now() - loadedAt < FRESH_MS) return;
  await refreshMarketConditions();
}

export function useMarketConditions(): State {
  useEffect(() => { void ensureMarketConditions(); }, []);
  return useSyncExternalStore(subscribe, () => state);
}

/** Markets with a written note, in canonical order. Used for the header count. */
export function activeMarkets(notes: MarketConditionsNotes): string[] {
  const hasNote = (market: string) => Boolean(notes[market]?.text?.trim());
  const known = markets.filter(hasNote);
  const extra = Object.keys(notes).filter(m => !markets.includes(m) && hasNote(m)).sort();
  return [...known, ...extra];
}

/** Every market, including those that currently show Normal Operations. */
export function allMarkets(notes: MarketConditionsNotes): string[] {
  return [...markets, ...Object.keys(notes).filter(m => !markets.includes(m)).sort()];
}

export interface MarketConditionRevision {
  id: number;
  market: string;
  day: string;
  conditions: string;
  condition_status: 'green'|'yellow'|'red';
  days_out: number | null;
  image_count: number | null;
  version: number;
  author: string;
  changed_at: string | null;
}

/** Every saved revision of one market on one operating day, newest first. */
export async function fetchMarketConditionsHistory(market: string, day: string): Promise<MarketConditionRevision[]> {
  const { data } = await api.get('/market-conditions/history', { params: { market, day } });
  return data?.rows ?? [];
}

export function marketImageUrl(id: number): string {
  const base = api.defaults?.baseURL ?? '';
  return `${base}/market-conditions/images/${id}`;
}
